import { useCallback, useEffect, useRef, useState } from 'react';
import { CloudUpload } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Typography,
} from '@mui/material';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { useSnackbar } from 'notistack';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

interface UploadFile {
  file: File;
  path: string;
}

interface OrganizationDataImportV2SectionProps {
  organizationId: string;
  /** Called after a successful upload so the imports list can refresh. */
  onUploaded?: () => void;
  /**
   * When set, append the files to this existing batch (raw upload id) instead
   * of creating a new one. Allowed only while the batch is still pre-conversion.
   */
  uploadId?: string;
  /** Render just the drop zone, without the surrounding Card chrome (e.g. in a dialog). */
  embedded?: boolean;
}

const isFileEntry = (e: FileSystemEntry): e is FileSystemFileEntry => e.isFile;
const isDirectoryEntry = (e: FileSystemEntry): e is FileSystemDirectoryEntry =>
  e.isDirectory;

/** Read every entry of a directory (readEntries returns at most ~100/call). */
function readAllEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const acc: FileSystemEntry[] = [];
    const readBatch = (): void => {
      reader.readEntries(batch => {
        if (batch.length === 0) {
          resolve(acc);
        } else {
          acc.push(...batch);
          readBatch();
        }
      }, reject);
    };
    readBatch();
  });
}

/** Recursively flatten a dropped file/folder entry into files + relative paths. */
async function entryToFiles(
  entry: FileSystemEntry,
  prefix: string,
): Promise<UploadFile[]> {
  if (isFileEntry(entry)) {
    const file = await new Promise<File>((resolve, reject) =>
      entry.file(resolve, reject),
    );
    return [{ file, path: `${prefix}${file.name}` }];
  }
  if (isDirectoryEntry(entry)) {
    const entries = await readAllEntries(entry.createReader());
    const groups = await Promise.all(
      entries.map(e => entryToFiles(e, `${prefix}${entry.name}/`)),
    );
    return groups.flat();
  }
  return [];
}

/**
 * Raw source-file upload for the migration agent. Drag & drop files or a whole
 * folder (or browse) — everything is staged straight into the S3 landing zone
 * (v2 raw prefix). Folder structure is preserved. What happens to an upload
 * afterward (convert → stage → commit) shows in the Imports list below.
 */
export function OrganizationDataImportV2Section({
  organizationId,
  onUploaded,
  uploadId,
  embedded = false,
}: OrganizationDataImportV2SectionProps) {
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // `webkitdirectory` isn't a typed React attribute — set it imperatively.
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
    }
  }, []);

  const uploadItems = useCallback(
    async (items: UploadFile[]) => {
      if (items.length === 0 || !organizationId) return;
      setUploading(true);
      try {
        const token = await getAuth0AccessTokenSilently();
        const form = new FormData();
        form.append('organization_id', organizationId);
        if (uploadId) form.append('upload_id', uploadId);
        for (const item of items) {
          form.append('file', item.file, item.path);
        }

        const res = await fetch(
          `${API_BASE_URL}/v2/admin/data-migration/raw-upload`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: form,
          },
        );

        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          throw new Error(body.error ?? `Upload failed (${res.status})`);
        }

        const data = (await res.json()) as {
          count: number;
          skipped?: string[];
        };
        const skipped = data.skipped?.length ?? 0;
        const verb = uploadId ? 'Added' : 'Uploaded';
        const message = skipped
          ? `${verb} ${data.count} file(s), skipped ${skipped} duplicate(s)`
          : `${verb} ${data.count} file(s)`;
        enqueueSnackbar(message, {
          variant: skipped ? 'warning' : 'success',
        });
        onUploaded?.();
      } catch (err) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Upload failed', {
          variant: 'error',
        });
      } finally {
        setUploading(false);
      }
    },
    [organizationId, uploadId, enqueueSnackbar, onUploaded],
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const dt = e.dataTransfer;
    // Grab entries synchronously — they become invalid after the handler ends.
    const entries: FileSystemEntry[] = [];
    for (const item of Array.from(dt.items)) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
    }
    const plainFiles = Array.from(dt.files);
    void (async () => {
      let collected: UploadFile[];
      if (entries.length > 0) {
        const groups = await Promise.all(entries.map(en => entryToFiles(en, '')));
        collected = groups.flat();
      } else {
        collected = plainFiles.map(file => ({ file, path: file.name }));
      }
      await uploadItems(collected);
    })();
  };

  const onPicked = (list: FileList | null) => {
    if (!list) return;
    const collected = Array.from(list).map(file => ({
      file,
      path: file.webkitRelativePath || file.name,
    }));
    void uploadItems(collected);
  };

  const dropZone = (
    <>
      <input
        ref={folderInputRef}
        type="file"
        hidden
        onChange={e => {
          onPicked(e.target.files);
          e.target.value = '';
        }}
      />

      <Box
        onDragOver={e => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={e => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={onDrop}
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          bgcolor: dragActive ? 'action.hover' : 'transparent',
        }}
      >
        <CloudUpload color="action" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag &amp; drop files or a folder here
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={
              uploading ? <CircularProgress size={16} /> : <CloudUpload />
            }
            disabled={uploading || !organizationId}
            onClick={() => folderInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose files'}
          </Button>
        </Box>
      </Box>
    </>
  );

  if (embedded) {
    return dropZone;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Upload source files"
        subheader="Drop raw source files or a folder straight into S3 — no processing. The migration agent converts them, then the result shows in Imports below."
      />
      <CardContent>{dropZone}</CardContent>
    </Card>
  );
}
